import { StrictMode, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App as AntApp, ConfigProvider } from 'antd';
import arEG from 'antd/locale/ar_EG';
import enGB from 'antd/locale/en_GB';
import { BarahPayApiError, createI18n } from '@barahpay/shared';
import { AuthProvider } from './auth/AuthContext';
import { LanguageProvider, useLanguage } from './i18n/LanguageContext';
import { ThemeProvider, useThemeMode } from './theme/ThemeContext';
import { AppRoutes } from './routes';
import { buildTheme } from './theme';
// The brand stylesheet carries the self-hosted fonts and the palette variables, so it loads first.
import '@barahpay/shared/brand.css';
import './styles.css';

createI18n(localStorage.getItem('barahpay.admin.language') ?? navigator.language);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // A 4xx will not fix itself; only transient failures are worth a second attempt.
        if (error instanceof BarahPayApiError && !error.isTransient) {
          return false;
        }

        return failureCount < 2;
      },
    },
    mutations: {
      // A payment operation is never retried automatically — the caller decides (spec section 53).
      retry: false,
    },
  },
});

/** Applies the Ant Design locale, direction and light/dark theme (spec sections 86 and 87). */
function ThemedApp() {
  const { language, rtl } = useLanguage();
  const { mode } = useThemeMode();
  const themeConfig = useMemo(() => buildTheme(mode), [mode]);

  return (
    <ConfigProvider direction={rtl ? 'rtl' : 'ltr'} locale={language === 'ar' ? arEG : enGB} theme={themeConfig}>
      <AntApp>
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
      </AntApp>
    </ConfigProvider>
  );
}

const container = document.getElementById('root');

if (!container) {
  throw new Error('The #root element is missing from index.html.');
}

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <ThemeProvider>
          <ThemedApp />
        </ThemeProvider>
      </LanguageProvider>
    </QueryClientProvider>
  </StrictMode>,
);
